/*************************************************************************
 * @license
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2026 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

/* global document: readonly */

const getBundleScript = () => Array.from(document.scripts)
    .find(script => /\/entai\.js(?:[?#]|$)/.test(script.src));

export function getBundleVersion() {
    const script = getBundleScript();
    if (!script) return null;
    const { pathname } = new URL(script.src);
    const match = pathname.match(/\/(\d+\.\d+\.\d+(?:-[\w.-]+)?(?:\+[\w.-]+)?)\/entai\.js$/);
    return match?.[1] ?? null;
}

/**
 * Resolves the URL of a file built as a sibling of the main bundle
 * (entai.js), e.g. excel-parser-worker.js or viewer-frame.js.
 *
 * There is no bundler-native `new URL(..., import.meta.url)` resolution here
 * (this app's esbuild output is IIFE, not ESM). Instead this mirrors
 * src/launcher.js's established technique: find the already-loaded <script>
 * tag for entai.js and derive the shared bundle directory from it. Every
 * secondary entry point is built into that same directory (see
 * scripts/esbuild.js), so wherever entai.js was loaded from is also where its
 * siblings were published.
 */
export function getBundleBaseUrl() {
    const entaiScript = getBundleScript();
    if (!entaiScript) {
        throw new Error('Could not locate entai.js script tag to resolve bundle asset URLs');
    }
    return entaiScript.src.replace(/entai\.js(?:[?#].*)?$/, '');
}

export function getBundleAssetUrl(fileName) {
    return `${getBundleBaseUrl()}${fileName}`;
}
